import React from 'react';
import LanguageIcon from '@mui/icons-material/Language';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import BusinessIcon from '@mui/icons-material/Business';
import MovieIcon from '@mui/icons-material/Movie';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import ComputerIcon from '@mui/icons-material/Computer';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import BookIcon from '@mui/icons-material/Book';
import Diversity1Icon from '@mui/icons-material/Diversity1';
import SchoolIcon from '@mui/icons-material/School';
import BuildIcon from '@mui/icons-material/Build';
import FactoryIcon from '@mui/icons-material/Factory';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import TranslateIcon from '@mui/icons-material/Translate';
import EuroIcon from '@mui/icons-material/Euro';
import LandscapeIcon from '@mui/icons-material/Landscape';
import FavoriteIcon from '@mui/icons-material/Favorite';
import HistoryIcon from '@mui/icons-material/History';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import BalanceIcon from '@mui/icons-material/Balance';
import LabIcon from '@mui/icons-material/Science';
import GroupsIcon from '@mui/icons-material/Groups';
import FunctionsIcon from '@mui/icons-material/Functions';
import PublicIcon from '@mui/icons-material/Public';
import ParkIcon from '@mui/icons-material/Park';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import ChurchIcon from '@mui/icons-material/Church';
import BiotechIcon from '@mui/icons-material/Biotech';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ConstructionIcon from '@mui/icons-material/Construction';
import PsychologyIcon from '@mui/icons-material/Psychology';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import SecurityIcon from '@mui/icons-material/Security';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';

// Mapping function to get the appropriate Material Icon for each program category
export const getCategoryIcon = (categoryName: string) => {
  const iconMap: { [key: string]: React.ComponentType<any> } = {
    'Aboriginal and Foreign Languages': LanguageIcon,
    'Agriculture': AgricultureIcon,
    'Architecture': ArchitectureIcon,
    'Biological and Biomedical Sciences': LabIcon,
    'Business, Management, and Marketing': BusinessIcon,
    'Communication and Media': MovieIcon,
    'Communications Technologies': PhoneInTalkIcon,
    'Computer Information Sciences': ComputerIcon,
    'Computer Systems Engineering': DeveloperBoardIcon,
    'Concurrent Education': BookIcon,
    'Cultural and Gender Studies': Diversity1Icon,
    'Education': SchoolIcon,
    'Engineering': BuildIcon,
    'Engineering Technologies': FactoryIcon,
    'English Language Literature': MenuBookIcon,
    'Family Consumer Human Sciences': RestaurantIcon,
    'Foreign Language Literature': TranslateIcon,
    'French Language and Literature': MenuBookIcon,
    'Geography': LandscapeIcon,
    'Health Professions & Clinical Science': FavoriteIcon,
    'History': HistoryIcon,
    'Interior Design': WallpaperIcon,
    'Legal Professions Studies': BalanceIcon,
    'Liberal Arts, Sciences, General': LabIcon,
    'Liberal Arts, Sciences, General Humanities': GroupsIcon,
    'Mathematics and Statistics': FunctionsIcon,
    'Multidisciplinary/Interdisciplinary Studies': PublicIcon,
    'Natural Resources and Conservation': ParkIcon,
    'Nursing': LocalHospitalIcon,
    'Other': AllInclusiveIcon,
    'Philosophy & Religious Studies': ChurchIcon,
    'Physical Sciences': BiotechIcon,
    'Political & Social Sciences': AssignmentIcon,
    'Professions & Trades': ConstructionIcon,
    'Psychology': PsychologyIcon,
    'Recreation, Leisure and Fitness Studies': DirectionsRunIcon,
    'Security and Protective Services': SecurityIcon,
    'Transportation and Materials Moving': LocalShippingIcon,
    'Visual & Performing Arts': TheaterComedyIcon,
  };
  
  return iconMap[categoryName] || AllInclusiveIcon; // Default to AllInclusiveIcon for unknown categories
};

// Component to render a category icon with the same styling as Program Categories page
export const CategoryIcon: React.FC<{ categoryName: string; size?: number }> = ({ 
  categoryName, 
  size = 16 
}) => {
  const IconComponent = getCategoryIcon(categoryName);
  
  return (
    <span 
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        backgroundColor: '#8dd439',
        color: '#000000',
        borderRadius: '50%',
        border: '1px solid #000000',
        marginRight: '4px',
        flexShrink: 0
      }}
    >
      <IconComponent style={{ fontSize: size * 0.75 }} />
    </span>
  );
};

// Function to generate HTML string for category icon (for use in messageParser)
export const getCategoryIconHTML = (categoryName: string, size: number = 16): string => {
  const iconMap: { [key: string]: string } = {
    'Aboriginal and Foreign Languages': 'language',
    'Agriculture': 'agriculture',
    'Architecture': 'architecture',
    'Biological and Biomedical Sciences': 'science',
    'Business, Management, and Marketing': 'business',
    'Communication and Media': 'movie',
    'Communications Technologies': 'phone_in_talk',
    'Computer Information Sciences': 'computer',
    'Computer Systems Engineering': 'developer_board',
    'Concurrent Education': 'book',
    'Cultural and Gender Studies': 'diversity_1',
    'Education': 'school',
    'Engineering': 'build',
    'Engineering Technologies': 'factory',
    'English Language Literature': 'menu_book',
    'Family Consumer Human Sciences': 'restaurant',
    'Foreign Language Literature': 'translate',
    'French Language and Literature': 'menu_book',
    'Geography': 'landscape',
    'Health Professions & Clinical Science': 'favorite',
    'History': 'history',
    'Interior Design': 'wallpaper',
    'Legal Professions Studies': 'balance',
    'Liberal Arts, Sciences, General': 'science',
    'Liberal Arts, Sciences, General Humanities': 'groups',
    'Mathematics and Statistics': 'functions',
    'Multidisciplinary/Interdisciplinary Studies': 'public',
    'Natural Resources and Conservation': 'park',
    'Nursing': 'local_hospital',
    'Other': 'all_inclusive',
    'Philosophy & Religious Studies': 'church',
    'Physical Sciences': 'biotech',
    'Political & Social Sciences': 'assignment',
    'Professions & Trades': 'construction',
    'Psychology': 'psychology',
    'Recreation, Leisure and Fitness Studies': 'directions_run',
    'Security and Protective Services': 'security',
    'Transportation and Materials Moving': 'local_shipping',
    'Visual & Performing Arts': 'theater_comedy',
  };
  
  const iconName = iconMap[categoryName] || 'all_inclusive';
  
  return `<span style="display: inline-flex; align-items: center; justify-content: center; width: ${size}px; height: ${size}px; background-color: #8dd439; color: #000000; border-radius: 50%; border: 1px solid #000000; margin-right: 4px; flex-shrink: 0; vertical-align: middle;"><span style="font-family: 'Material Icons'; font-size: ${size * 0.75}px; font-style: normal; font-weight: normal; line-height: 1; letter-spacing: normal; text-transform: none; display: inline-block; white-space: nowrap; word-wrap: normal; direction: ltr; -webkit-font-feature-settings: 'liga'; -webkit-font-smoothing: antialiased;">${iconName}</span></span>`;
};
